import { z } from "zod";

export const PartnerCreditInstallmentSchema = z.object({
  installmentNo: z.number().int().positive(),
  label: z.string().max(100).optional(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຮູບແບບ"),
  amount: z.coerce.number().int().positive("ຍອດງວດຕ້ອງຫຼາຍກວ່າ 0"),
});

export const PartnerPricingStatusSchema = z.enum(["draft", "quoted", "locked"]);
export type PartnerPricingStatus = z.infer<typeof PartnerPricingStatusSchema>;

export const PartnerProjectSaleSchema = z.object({
  id: z.string(),
  pricingStatus: PartnerPricingStatusSchema,
  provisionalTotal: z.number().int().nullable(),
  price: z.number().int().nullable(),
  finalPrice: z.number().int().nullable(),
  discountType: z.enum(["percentage", "fixed"]).nullable(),
  discountValue: z.number().nullable(),
  discountAmount: z.number().int().nullable(),
  partnerLabel: z.string().nullable(),
  offSiteDistance: z.enum(["near", "far"]).nullable(),
  notes: z.string().nullable(),
  startDate: z.string(),
  expireDate: z.string(),
  classTemplateIds: z.array(z.string()),
  instructorIds: z.array(z.string()),
  classLabels: z.array(z.string()),
  instructorLabels: z.array(z.string()),
  quotedAt: z.coerce.date().nullable(),
  lockedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  creditInvoiceId: z.string().nullable().optional(),
  receiptId: z.string().nullable().optional(),
});
export type PartnerProjectSaleDTO = z.infer<typeof PartnerProjectSaleSchema>;

export const UpdatePartnerProjectPricingSchema = z
  .object({
    pricingStatus: z.literal("quoted").optional(),
    price: z.coerce.number().int().min(1, "ລາຄາຕ້ອງຫຼາຍກວ່າ 0").optional(),
    provisionalTotal: z.coerce.number().int().min(0).nullable().optional(),
    discountType: z.enum(["percentage", "fixed"]).nullable().optional(),
    discountValue: z.number().nullable().optional(),
  })
  .refine(
    (data) =>
      data.price != null ||
      data.provisionalTotal !== undefined ||
      data.pricingStatus != null ||
      data.discountType !== undefined,
    { message: "ຕ້ອງມີການປ່ຽນແປງຢ່າງນ້ອຍ 1 ຟິວ" },
  );
export type UpdatePartnerProjectPricingDTO = z.infer<
  typeof UpdatePartnerProjectPricingSchema
>;

export const LockPartnerProjectPricingSchema = z
  .object({
    price: z.coerce.number().int().min(1, "ລາຄາຕ້ອງຫຼາຍກວ່າ 0"),
    discountType: z.enum(["percentage", "fixed"]).optional(),
    discountValue: z.number().optional(),
    settlementMode: z.enum(["receipt", "credit"]).default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
    installments: z.array(PartnerCreditInstallmentSchema).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.settlementMode !== "credit") return;

    let finalPrice = data.price;
    if (data.discountType && data.discountValue !== undefined) {
      let discountAmount = 0;
      if (data.discountType === "percentage") {
        discountAmount = Math.round((data.price * data.discountValue) / 100);
      } else {
        discountAmount = Math.round(data.discountValue);
      }
      discountAmount = Math.min(discountAmount, data.price);
      finalPrice = data.price - discountAmount;
      if (finalPrice < 0) finalPrice = 0;
    }

    if (finalPrice <= 0) return;

    const rows = data.installments;
    if (!rows || rows.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງກຳນົດແຜນງວດຊຳລະ (ຢ່າງນ້ອຍ 2 ງວດ)",
        path: ["installments"],
      });
    }
  });
export type LockPartnerProjectPricingDTO = z.infer<
  typeof LockPartnerProjectPricingSchema
>;

export const PartnerProjectSaleIdParamSchema = z.object({
  id: z.string().min(1),
});

export const PartnerProjectSaleListItemSchema = z.object({
  id: z.string(),
  partnerLabel: z.string().nullable(),
  pricingStatus: PartnerPricingStatusSchema,
  provisionalTotal: z.number().int().nullable(),
  price: z.number().int().nullable(),
  finalPrice: z.number().int().nullable(),
  offSiteDistance: z.enum(["near", "far"]).nullable(),
  startDate: z.string(),
  expireDate: z.string(),
  classCount: z.number().int(),
  createdAt: z.string(),
});
export type PartnerProjectSaleListItemDTO = z.infer<
  typeof PartnerProjectSaleListItemSchema
>;
